import {
	Card,
	CardHeading,
	IconButton,
	IconLinkButton,
	Loading,
	SwitchControl,
	Trigger,
	useEditDrawer,
} from "@dgs/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useFilterQuery } from "../../../shared/api/filterQueries";
import {
	CalendarBody,
	CalendarCardHeader,
	CalendarHeaderActions,
	CalendarNavigation,
	CalendarProvider,
	EditCalendarSettingDrawer,
	EventPopover,
	useCalendarEvents,
	useCalendarNavigationOptions,
	useCalendarProvider,
	useEventPopover,
	useHolidays,
} from "../../../shared/calendar";
import { ICalendarEvent } from "../../../shared/calendar/CalendarComponents";
import { CreateEventDrawer } from "../../../shared/events/save/CreateEventDrawer";
import { useFilter } from "../../../shared/filters";
import { EventFilterDrawer } from "../../../shared/filters/events/EventFilterDrawer";
import { FilterBar } from "../../../shared/filters/FilterBar";
import { eventService } from "../shared/api/event/eventService";
import { eventCalendarSettingService } from "../shared/api/eventCalendarSettingService";
import { EventScopeProps } from "./overview/EventsOverview";
import { useEventSave } from "./useEventSave";

const StyledSwitchControl = styled(SwitchControl)`
	margin: 0;
`;

export const EventCalendarPage: FC<EventScopeProps> = (props) => {
	const { data: calendarSettings } = useQuery({
		queryFn: () => eventCalendarSettingService.show().then((x) => x.data.data),
		queryKey: eventCalendarSettingService.showKey,
	});

	if (!calendarSettings) {
		return <Loading />;
	}

	return (
		<CalendarProvider settings={{ ...calendarSettings, yasumiProviderId: calendarSettings.yasumiProvider.id }}>
			<EventCalendar {...props} />
		</CalendarProvider>
	);
};

const FilterContainer = styled.div`
	margin-left: ${({ theme }) => theme.spacing(3)};
`;

const EventCalendar: FC<EventScopeProps> = ({ showOnlyOwnEvents, onToggle }) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const state = useCalendarProvider();
	const events = useCalendarEvents(showOnlyOwnEvents);
	const holidays = useHolidays();
	const calendarOptions = useCalendarNavigationOptions(state.selectedDate);
	const onSettingsUpdateSuccess = () => queryClient.invalidateQueries({ queryKey: eventService.calendarKey });
	const { openPopover, popoverProps } = useEventPopover();
	const { activeFilterId, resetFilter, applyFilter } = useFilter();
	const filterDrawerProps = useEditDrawer();
	const { data: predefinedFilter } = useFilterQuery({ filterId: activeFilterId });
	const handleSave = useEventSave();
	const openFilterDrawer = useCallback(() => {
		filterDrawerProps.handleOpen(activeFilterId || "new");
	}, [activeFilterId, filterDrawerProps]);

	return (
		<>
			<Card>
				<CalendarCardHeader>
					<CardHeading>{t("Event calendar")}</CardHeading>
					<CalendarNavigation {...calendarOptions} />
					<CalendarHeaderActions>
						<StyledSwitchControl
							name="showOnlyOwnEvents"
							label={t("Only my events")}
							value={showOnlyOwnEvents}
							onChange={onToggle}
						/>
						<CreateEventDrawer createPath="/events/create" detailsPath="/events/active" handleSubmit={handleSave} />
						<IconButton title={t("Open event filter")} onClick={openFilterDrawer} icon="filter" size="small" />
						<Trigger
							render={(props) => (
								<EditCalendarSettingDrawer
									open={props.open}
									onClose={props.handleClose}
									onUpdateSuccess={onSettingsUpdateSuccess}
								/>
							)}
						>
							{(props) => (
								<IconButton
									size="small"
									title={t("Event calendar settings")}
									type="button"
									icon="settings"
									{...props}
								/>
							)}
						</Trigger>
					</CalendarHeaderActions>
				</CalendarCardHeader>
				<FilterContainer>
					<FilterBar filterId={activeFilterId} onRemove={resetFilter} onClick={openFilterDrawer} />
				</FilterContainer>
				<CalendarBody
					onEventClick={(e, calendarEvent) => {
						if (calendarEvent.type === "event") {
							openPopover(e, calendarEvent.id);
						}
					}}
					events={[
						...events.map(
							(x): ICalendarEvent => ({
								...x,
								color: x.eventType.color,
								type: "event",
							}),
						),
						...holidays.map(
							(x): ICalendarEvent => ({
								...x,
								type: "holiday",
								participationStatus: "none",
							}),
						),
					]}
				/>
				<EventPopover
					{...popoverProps}
					event={events.find((event) => popoverProps.eventId === event.id) || null}
					renderActions={(event) => (
						<IconLinkButton to={`/events/active/${event.id}/details`} icon="eye" title={t("To event")} size="small" />
					)}
				/>
			</Card>
			{filterDrawerProps.editEntityId && (
				<EventFilterDrawer {...filterDrawerProps} filter={predefinedFilter || null} onSubmit={applyFilter} />
			)}
		</>
	);
};
